import { TRAINING } from '../content';
import type { Section, Topic } from '../content/types';
import { visibleSections, type TrainingViewer } from './access';

/**
 * Guided mode: the guide as one screen at a time instead of one long scroll.
 *
 * WHY BOTH MODES EXIST. The guide does two jobs that want opposite layouts.
 * Onboarding is read once, in order, in someone's first week — it wants one
 * thing at a time and a sense of progress. Lookup ("how do I confirm a concrete
 * order?") wants a single searchable page you can Ctrl+F and deep-link into,
 * which is exactly what the `?` Help drawer does. A PM's page is 64 topic cards
 * and ~5,600 words, and 12 of those cards come before anything about their own
 * job (product owner, 2026-08-03: "there is a lot of scrolling with how it's laid out").
 * So guided mode is a second renderer over the same content, not a replacement.
 *
 * ⚠️ THE APPENDIX IS DELIBERATELY EXCLUDED. It is a reference — you go to it to
 * look one screen up. Clicking Next 31 times through it would be worse than the
 * scroll it was meant to fix. Guided mode covers foundations and the reader's
 * own track, then hands them to the reference.
 *
 * ⚠️ Nothing here re-states content. Screens are built from the same Topic
 * objects the page and the Word export read, which is why a content fix lands
 * in all three at once.
 */

export type GuidedScreen =
  /** A section title, so the structure still reads when there's no scroll to show it. */
  | { kind: 'section'; id: string; title: string; intro?: string; part: string; topicCount: number }
  /** One topic — the unit that gets ticked. */
  | { kind: 'topic'; id: string; topic: Topic; part: string; sectionTitle: string }
  /** The end, with what to do next. */
  | { kind: 'done'; id: string; part: string };

/**
 * Flatten what this reader should walk through into an ordered screen list.
 *
 * Foundations first, then their chosen track — the same order the page renders,
 * so the two modes never disagree about what comes before what.
 */
export function guidedScreens(v: TrainingViewer, trackId: string | undefined): GuidedScreen[] {
  const out: GuidedScreen[] = [];

  const push = (sections: Section[], part: string) => {
    for (const s of sections) {
      if (!s.topics.length) continue;
      out.push({ kind: 'section', id: `sec-${s.id}`, title: s.title, intro: s.intro, part, topicCount: s.topics.length });
      for (const t of s.topics) out.push({ kind: 'topic', id: t.id, topic: t, part, sectionTitle: s.title });
    }
  };

  const track = TRAINING.tracks.find((t) => t.id === trackId);

  // ⚠️ The Field Leader track is scoped by TRACK, not by reader, and that scope
  // covers the FOUNDATIONS as well — TrainingPage resolves one viewer and renders
  // both parts through it. Scoping only the track here made a PM previewing the
  // field-leader walkthrough read two company-wide foundation topics a field
  // leader never sees ("two levels", "my projects vs all"), so the walkthrough
  // and the page disagreed about the same track (caught by guided.spec).
  const tv: TrainingViewer = track?.id === 'field-leader' ? { ...v, forceFieldLeaderScope: true } : v;

  push(visibleSections(tv, TRAINING.foundations), 'Everyone starts here');
  if (track) push(visibleSections(tv, track.sections), track.label);

  if (out.length) out.push({ kind: 'done', id: '__done', part: track?.label ?? 'Everyone starts here' });
  return out;
}

/**
 * Where a given topic sits in the sequence, or -1.
 *
 * Used to come back to the exact slide someone left when they followed a "Take
 * me there" link — resuming by progress would skip it if they'd ticked it first.
 */
export const indexOfTopic = (screens: GuidedScreen[], topicId: string | null | undefined): number =>
  topicId ? screens.findIndex((s) => s.kind === 'topic' && s.id === topicId) : -1;

/** Just the topic ids, in order — what progress is measured against. */
export const guidedTopicIds = (screens: GuidedScreen[]): string[] =>
  screens.filter((s): s is Extract<GuidedScreen, { kind: 'topic' }> => s.kind === 'topic').map((s) => s.id);

/**
 * Where to resume: the first topic screen the reader hasn't ticked.
 *
 * Returning the first UNREAD topic rather than saving a cursor means progress is
 * the only state — nothing to keep in sync, and it does the right thing if
 * content is added later. Returns 0 for a first-timer and the final screen for
 * someone who has finished.
 */
export function resumeIndex(screens: GuidedScreen[], isDone: (topicId: string) => boolean): number {
  const firstUnread = screens.findIndex((s) => s.kind === 'topic' && !isDone(s.id));
  if (firstUnread === -1) return Math.max(screens.length - 1, 0);
  // Land on the section heading when the unread topic is the first in its
  // section — arriving mid-section with no heading loses the context.
  const prev = screens[firstUnread - 1];
  return prev?.kind === 'section' ? firstUnread - 1 : firstUnread;
}
